import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';

const BlogDetail = () => {
  const { id } = useParams(); 
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState('');
  const [readTime, setReadTime] = useState('N/A');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  useEffect(() => {
    const today = new Date();
    const formattedDate = today.toLocaleDateString('en-US', {year: 'numeric', month: 'long', day: 'numeric'});
    setCurrentDate(formattedDate);

    const fetchBlog = async () => {
      setLoading(true);
      setError(null); 
      try {
        const response = await fetch(`${API_URL}/blog/${id}`);
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();
        if (data) {
          setBlog(data);
          const content = data.fullContent || data.content || '';
          const wordsPerMinute = 200;
          const wordCount = content.split(/\s+/).filter(word => word !== '').length;
          setReadTime(`${Math.ceil(wordCount / wordsPerMinute)} min read`);
        } else {
          throw new Error('Blog not found for this ID.');
        }
      } catch (err) {
        setError(err.message || "An unexpected error occurred.");
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchBlog();
    else {
      setLoading(false);
      setError("No blog ID provided.");
    }
  }, [id, API_URL]);

  const getThreatColor = (threat) => {
    switch (threat?.toUpperCase()) {
      case 'CRITICAL': return 'text-red-600 border-red-200 bg-red-50';
      case 'HIGH': return 'text-orange-600 border-orange-200 bg-orange-50';
      case 'MEDIUM': return 'text-yellow-500 border-yellow-200 bg-yellow-50';
      case 'LOW': return 'text-green-600 border-green-200 bg-green-50';
      default: return 'text-cyan-600 border-cyan-200 bg-cyan-50';
    }
  };

  // Loader
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span className="text-cyan-700 text-lg font-bold">Loading...</span>
        </div>
      </div>
    );
  }
  // Error
  if (error || !blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white p-5">
        <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow mb-8 w-full max-w-lg text-center">
          <svg className="w-7 h-7 text-red-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-base font-semibold">{error || "Blog not found."}</div>
        </div>
        <Link 
          to="/blogs"
          className="inline-flex items-center gap-2 px-7 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-700 text-white font-bold shadow hover:from-cyan-600 hover:to-cyan-400 transition"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          BACK TO ALL BLOGS
        </Link>
      </div>
    );
  }
  
  return (
    <section className="min-h-screen bg-white text-black font-sans">
      <div className="w-full mx-auto flex flex-col items-center px-2 sm:px-6 lg:px-12 py-10">
        {/* Back Button */}
        <div className="w-full max-w-5xl mb-6">
          <Link 
            to="/blogs"
            className="inline-flex items-center gap-2 text-cyan-700 hover:text-black font-medium transition text-base"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
            </svg>
            Back to all blogs
          </Link>
        </div>
        {/* Full-width Blog Card */}
        <article className="w-full max-w-5xl bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden transition-all duration-300">
          <div className="relative w-full">
            <img
              src={blog.iconUrl}
              alt={blog.heading}
              className="w-full h-64 sm:h-96 object-cover"
              style={{maxHeight:"400px"}}
            />
            {blog.threat_level && (
              <span className={`absolute top-5 right-5 px-4 py-2 rounded-full text-xs font-bold border shadow ${getThreatColor(blog.threat_level)}`}>
                THREAT: {blog.threat_level?.toUpperCase()}
              </span>
            )}
          </div>
          <div className="w-full px-6 md:px-14 py-7 sm:py-10">
            {/* Category Tag */}
            {blog.category && (
              <div className="inline-block bg-cyan-100 text-cyan-700 font-bold rounded-full px-4 py-2 mb-4 text-xs">
                {blog.category.replace('_',' ').toUpperCase()}
              </div>
            )}
            {/* Heading */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-2 leading-tight tracking-tight">
              {blog.heading}
            </h1>
            {/* Meta Data */}
            <div className="flex flex-wrap gap-6 sm:gap-11 text-gray-500 text-sm border-b border-gray-200 pb-4 mb-6">
              <span>
                By <span className="text-black font-bold">{blog.author || 'Glocybs Team'}</span>
              </span>
              <span>{currentDate}</span>
              <span>{readTime}</span>
            </div>
            {/* Blog Content */}
            <div
              className="prose prose-lg blog-content-area max-w-none text-gray-900"
              dangerouslySetInnerHTML={{
                __html: blog.fullContent || blog.content || "<p>Full content is not available.</p>"
              }}
            />
            {/* Action Buttons */}
            <div className="border-t border-gray-100 pt-7 mt-8 flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                className="flex-1 text-center px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-cyan-700 text-white font-bold shadow hover:from-cyan-600 hover:to-cyan-400 transition"
              >
                CONTACT US
              </a>
              <a
                href="#subscribe"
                className="flex-1 text-center px-4 py-3 rounded-xl border-2 border-cyan-400 text-cyan-700 font-bold hover:bg-cyan-50 transition"
              >
                SUBSCRIBE TO ALERTS
              </a>
            </div>
          </div>
        </article>
      </div>
      {/* Scoped Styles */}
      <style>{`
        .blog-content-area h2, 
        .blog-content-area h3,
        .blog-content-area h4 {
          color: #06b6d4;
          font-weight: bold;
          margin-top: 2rem;
          margin-bottom: 1rem;
        }
        .blog-content-area h2 { font-size: 2rem; }
        .blog-content-area h3 { font-size: 1.3rem; }
        .blog-content-area h4 { font-size: 1.15rem; }
        .blog-content-area ul, .blog-content-area ol {
          margin-bottom: 1rem;
          padding-left: 1.25rem;
        }
        .blog-content-area ul { list-style-type: disc;}
        .blog-content-area ol { list-style-type: decimal; }
        .blog-content-area a {
          color: #06b6d4;
          text-decoration: underline;
        }
        .blog-content-area a:hover {
          color: #f100ff;
        }
        .blog-content-area p {
          margin-bottom: 1rem;
          line-height: 1.7;
          font-size: 1.125rem;
        }
        .blog-content-area li {
          margin-bottom: 0.4rem;
        }
        @media (max-width: 639px) {
          .blog-content-area h2 { font-size: 1.35rem; }
          .blog-content-area h3 { font-size: 1.12rem; }
        }
      `}</style>
    </section>
  );
};

export default BlogDetail;
